import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import {
  FlexCol,
  FlexRow,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { Title } from '../General/Title';
import { TextP } from '../../Defaults/Text';
import { FormInput } from '../Form/FormInput';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { Tag, TagType } from '../Product/Tag';
import InfoRow from './InfoRow';
import { SaleBubble } from './SaleBubble';
import { FreeDelivery } from '../Product/FreeDelivery';
import { SaleBubbleNew } from './SaleBubbleNew';
import { WebP } from '../../Defaults/WebP';

interface Props {
  title: string;
  desc: string;
  plu: string;
  initialUserOrderNr: string;
  handleCustomOrderNrBlurChange: (e) => void;
  theme: any;
  shareButton: React.Component;
  bottomRef: any;
  issaleout: number;
  isnew: number;
  issale: number;
  isLoggedIn: boolean;
  children: any;
  openModal: any;
  specialDiscount: string;
  freeDelivery: boolean;
  isInCart: boolean;
  showFullImage?: boolean;
  picture: string;
}

function LeftUpper({
  title,
  desc,
  plu,
  initialUserOrderNr,
  handleCustomOrderNrBlurChange,
  theme,
  shareButton,
  bottomRef,
  isnew,
  issaleout,
  issale,
  isLoggedIn,
  children,
  openModal,
  specialDiscount,
  freeDelivery,
  isInCart,
  showFullImage,
  picture,
}: Props) {
  const onMoreDescClick = () => {
    if (bottomRef && bottomRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: bottomRef.current.offsetTop,
      });
    }
  };

  return (
    <Wrapper>
      <Title marginTop={0}>{title} </Title>
      {isInCart && (
        <IsInCartAlready>
          {__('Produkt sa nachádza v košíku')}
          <SvgIcon
            icon={IconType.cart}
            fill={theme.colors.primary}
            marginLeft={8}
            alt={__('Produkt sa nachádza v košíku')}
            title={__('Produkt sa nachádza v košíku')}
          />
        </IsInCartAlready>
      )}
      {freeDelivery && <FreeDelivery />}
      <ImgDescWrapper>
        <ImageWithPhotogallery>
          <ImageWrapper onClick={openModal}>
            <WebP
              image={picture}
              alt={`Obrázok ku produktu ${title}`}
              width={288}
              height={320}
            />
            <TagWrapper>
              {isnew ? (
                <TagRelativeWrapper>
                  <Tag text={'Novinka'} type={TagType.NEW} />
                </TagRelativeWrapper>
              ) : null}
              {issaleout ? (
                <TagRelativeWrapper>
                  <Tag text={'Výpredaj'} type={TagType.SALEOUT} />
                </TagRelativeWrapper>
              ) : null}
              {issale ? (
                <TagRelativeWrapper>
                  <Tag text={'Akcia'} type={TagType.ACTION} />
                </TagRelativeWrapper>
              ) : null}
            </TagWrapper>
            {specialDiscount && (issaleout || issale) ? (
              <SaleBubbleNew text={specialDiscount} />
            ) : null}
          </ImageWrapper>
          {children}
        </ImageWithPhotogallery>
        <DescPluWrapper>
          {desc && typeof desc === 'string' && (
            <>
              <Description
                dangerouslySetInnerHTML={{ __html: `${desc} ...` }}
              />
              <SpanShowMoreDesc onClick={onMoreDescClick}>
                {__('Viac')}
              </SpanShowMoreDesc>
            </>
          )}
          <InfoRow
            title={__('Kód produktu')}
            value={plu}
            hasBorderTop={desc ? desc.length > 0 : false}
          />
          <UserPluWrapper>
            {isLoggedIn && (
              <>
                <FormInput
                  name="userPlu"
                  type="text"
                  label={__('Vlastný kód produktu')}
                  maxWidthPx={320}
                  marginBottom={24}
                  onBlur={handleCustomOrderNrBlurChange}
                  defaultValue={initialUserOrderNr}
                />
                <SvgIcon
                  width={24}
                  height={24}
                  icon={IconType.success}
                  alt=""
                  title={__('Tu zadajte vlastný kód produktu')}
                  fill={theme.colors.borderColor}
                  marginLeft={16}
                />
              </>
            )}
          </UserPluWrapper>
          {shareButton}
        </DescPluWrapper>
      </ImgDescWrapper>
    </Wrapper>
  );
}

export const IsInCartAlready = styled(FlexRowCenterVertical)`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${rem(24)};
`;

const Wrapper = styled(FlexCol)`
  width: 100%;
  color: #aaaaaa;
  position: relative;
  margin-right: ${rem(32)};

  &:focus-within {
    color: ${props => props.theme.colors.btnPrimary};
  }

  ${({ theme }) => theme.mediab.l925`
    margin: 0;
    margin-bottom: ${rem(32)};
  `}

  ${({ theme }) => theme.mediab.s450`
    max-width: initial;
  `}
`;

const ImageWithPhotogallery = styled(FlexCol)`
  margin-right: ${rem(48)};
`;

const ImageWrapper = styled.div`
  max-width: ${rem(328)};
  min-width: ${rem(288)};
  height: ${rem(328)};
  width: 100%;
  position: relative;
  cursor: pointer;
  border: ${({ theme }) => theme.borders.primary};
  border-radius: ${rem(4)};
`;

const ImgDescWrapper = styled(FlexRow)`
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
    flex-flow: column;
  `}
`;

const DescPluWrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(448)};
`;

const TagRelativeWrapper = styled.div`
  margin-right: ${rem(8)};
`;

const TagWrapper = styled(FlexRow)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};
`;

const Text = styled(TextP)`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
`;

const UserPluWrapper = styled(FlexRowCenterVertical)`
  margin-top: ${rem(16)};
`;

const Description = styled.div``;

const SpanShowMoreDesc = styled.span`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  margin-bottom: ${rem(24)};
`;

const WithThemeLeftUpper: any = withTheme(LeftUpper);
export { WithThemeLeftUpper as LeftUpper };
